import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { caseStudyAPI } from '../../features/case-study/api/casestudyAPI';
import { APIError } from '../../lib/api/APIService';
import { API_CONFIG } from '../../config/api';
import UserProfileDropdown from '../UserProfileDropdown/UserProfileDropdown';
import { useAuth } from '../../features/auth/hooks/useAuth';
import CaseTutorialPanel from './CaseTutorialPanel';
import CaseDiscussionPanel from './CaseDiscussionPanel';
import CaseQuizPanel from './CaseQuizPanel';
import { useProductBackHandler } from '../ProductBackButton/ProductBackProvider';
import './CaseStudy.css';
import Logo_white from '../Logo/Logo';
import { stripDangerousHtml } from '../../lib/stripDangerousHtml';

// Shared between the wheel and touch section-paging handlers, so their
// scroll-vs-page thresholds and scroll-detection logic can't drift apart.
const SECTION_PAGE_SETTLE_MS = 700;
const SECTION_PAGE_COOLDOWN_MS = 620;
const SWIPE_MIN_DISTANCE_PX = 70;
// Mobile/iPad URL-bar and subpixel layout often leave 5–20px of "fake"
// remaining room at the true bottom. A 2px slack never clears there, so
// exhibit→exhibit paging never fires on touch even after the user finishes
// scrolling the exhibit. Keep this shared for wheel + touch.
const SCROLL_EDGE_SLACK_PX = 24;

const getElementScrollMetrics = (el) => {
  const scrollTop = el.scrollTop || 0;
  const clientHeight = el.clientHeight || 0;
  const scrollHeight = el.scrollHeight || 0;
  const maxScroll = Math.max(0, scrollHeight - clientHeight);
  return { scrollTop, maxScroll };
};

const elementConsumesScrollInDirection = (metrics, deltaY) => {
  // Ignore phantom 1–N px overflow that can never meaningfully scroll.
  if (metrics.maxScroll <= SCROLL_EDGE_SLACK_PX) return false;
  if (deltaY > 0) return metrics.scrollTop < metrics.maxScroll - SCROLL_EDGE_SLACK_PX;
  if (deltaY < 0) return metrics.scrollTop > SCROLL_EDGE_SLACK_PX;
  return false;
};

// Pure DOM logic, no component-state dependency — walks up from `target`
// looking for a `.natively-scrollable` ancestor that still has room to
// scroll in the gesture's direction (deltaY sign: positive = scrolling
// down/advancing, negative = scrolling up/retreating).
const isScrollableAncestorConsumingWheel = (target, deltaY) => {
  let el = target?.nodeType === Node.TEXT_NODE ? target.parentElement : target;
  while (el && el !== document.documentElement) {
    if (el.classList && el.classList.contains('natively-scrollable')) {
      const style = window.getComputedStyle(el);
      const oy = style.overflowY;
      if (oy === 'auto' || oy === 'scroll' || oy === 'overlay') {
        if (elementConsumesScrollInDirection(getElementScrollMetrics(el), deltaY)) {
          return true;
        }
      }
    }
    el = el.parentElement;
  }
  // Fallback: no internally-scrolling ancestor claimed this gesture, but the
  // window/document itself may still have genuine scroll room (it's the real
  // scroll container for content whose .natively-scrollable wrapper switches
  // to overflow:visible at mobile/tablet breakpoints). Irrelevant while an
  // overlay is open on top of the page — its own scroll room is what matters
  // there, already handled by the ancestor walk above.
  if (
    target?.closest?.(
      '.exhibit-modal-backdrop, .exhibit-modal, .diagram-viewer-backdrop, .diagram-viewer'
    )
  ) {
    return false;
  }
  const doc = document.documentElement;
  // Prefer the live window/visual viewport on iOS Safari: documentElement
  // clientHeight drifts with the dynamic URL bar and often never reports
  // "at bottom" even when the user has scrolled as far as they can.
  const scrollTop = Math.max(
    window.scrollY || 0,
    doc.scrollTop || 0,
    document.body.scrollTop || 0
  );
  const scrollHeight = Math.max(doc.scrollHeight || 0, document.body.scrollHeight || 0);
  const viewportHeight =
    (window.visualViewport && window.visualViewport.height) ||
    window.innerHeight ||
    doc.clientHeight ||
    0;
  return elementConsumesScrollInDirection(
    { scrollTop, maxScroll: Math.max(0, scrollHeight - viewportHeight) },
    deltaY
  );
};

// Display-only: strips a leading "Act I:"/"Exhibit 1:" style prefix from a
// heading, since the mobile eyebrow above it already shows that same label.
// No-ops (returns the original string) if no recognized prefix+separator is
// present, so titles that don't follow this convention are never mangled.
const stripSectionLabelPrefix = (title) => {
  if (!title) return title;
  return String(title)
    .replace(/^\s*(Act\s+[IVX]+|Exhibit\s+\d+)\s*[:.\-–]\s*/i, '')
    .trim();
};

const generateClientFallbackFeedback = (reasoning) => {
  const reasoningLength = reasoning.trim().length;
  
  const openings = reasoningLength < 50 ? [
    "Your analysis demonstrates initial strategic thinking, though there's opportunity to develop these ideas further.",
    "You've identified core considerations that many students overlook at first glance.",
    "This reasoning shows you're engaging with the fundamental tensions in the case.",
  ] : reasoningLength < 150 ? [
    "Your analysis shows solid engagement with the case dynamics and presents a coherent line of reasoning.",
    "You've developed a thoughtful perspective that balances multiple competing factors effectively.",
    "This reasoning demonstrates good analytical depth and consideration of various stakeholder impacts.",
  ] : [
    "Your comprehensive analysis demonstrates sophisticated strategic thinking and thorough consideration of complex variables.",
    "You've constructed a well-reasoned argument that shows deep engagement with the case materials and strategic implications.",
    "This detailed reasoning reflects advanced analytical skills and nuanced understanding of business decision-making.",
  ];
  
  const evidence = [
    "To strengthen future analyses, consider explicitly connecting your reasoning to specific data points or exhibits referenced in the case.",
    "Your reasoning would benefit from more direct references to case evidence and concrete supporting details.",
    "Consider how the available case materials might reinforce or challenge the assumptions underlying your analysis.",
  ];
  
  const strategic = [
    "Your decision-making process shows clear logical progression. Consider examining potential implementation challenges that might arise.",
    "The analytical framework you've applied demonstrates systematic thinking. Building on this, explore how different stakeholders might respond.",
    "Your approach effectively balances competing priorities. Consider developing contingency plans for changing circumstances.",
  ];
  
  const closing = [
    "Remember that multiple valid approaches exist - the strength lies in your reasoning process and evidence-based conclusions.",
    "Continue building your analytical toolkit by practicing systematic evaluation of complex business scenarios.",
    "Strong business analysis combines analytical rigor with practical wisdom about implementation realities.",
  ];
  
  const randomChoice = arr => arr[Math.floor(Math.random() * arr.length)];
  
  return `${randomChoice(openings)} ${randomChoice(evidence)} ${randomChoice(strategic)} ${randomChoice(closing)}`;
};

const SCORE_DISPLAY_ORDER = [
  'evidence_use',
  'logic_and_causality',
  'trade_off_analysis',
  'feasibility_and_execution',
  'risk_awareness',
  'overall',
];

const SCORE_LABELS = {
  evidence_use: 'Evidence use',
  logic_and_causality: 'Logic and causality',
  trade_off_analysis: 'Trade-off analysis',
  feasibility_and_execution: 'Feasibility and execution',
  risk_awareness: 'Risk awareness',
  overall: 'Overall',
};

const formatOverallVerdict = (verdict) => {
  if (!verdict) return '';
  if (typeof verdict === 'string') return verdict;
  if (typeof verdict === 'object') {
    const sentence = verdict.sentence || verdict.summary || '';
    const result = verdict.result ? `${verdict.result.toUpperCase()}: ` : '';
    return `${result}${sentence}`.trim();
  }
  return String(verdict);
};

const humanizeConceptLabel = (raw) => {
  if (!raw) return '';
  let s = String(raw).trim();
  s = s.replace(/^(lemma_|RG_|TG_|KG_|LG_)/i, '');
  s = s.replace(/^[KJLN]\d+_/, '');
  s = s.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const formatMissedItem = (item) => {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  if (typeof item === 'object') {
    const exhibit = item.exhibit_id ? `${item.exhibit_id}` : '';
    const concepts = Array.isArray(item.concepts_absent) && item.concepts_absent.length
      ? item.concepts_absent
      : Array.isArray(item.kg_nodes_absent)
        ? item.kg_nodes_absent.map(humanizeConceptLabel)
        : [];
    const conceptBit = concepts.length ? ` (${concepts.join('; ')})` : '';
    const gap = item.gap || item.miss || '';
    return [exhibit + conceptBit, gap].filter(Boolean).join(': ').trim();
  }
  return String(item);
};

const formatHowToFixItem = (item) => {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  if (typeof item === 'object') {
    const label = item.addresses_gap ? `${item.addresses_gap}: ` : '';
    return `${label}${item.action || ''}`.trim();
  }
  return String(item);
};

const formatIsomorphism = (iso) => {
  if (!iso) return null;
  if (typeof iso === 'number') return { total: iso };
  if (typeof iso === 'object') return iso;
  return null;
};

const activationPlain = (raw) => {
  const s = String(raw || '').toLowerCase();
  if (s === 'spontaneous') return 'used spontaneously';
  if (s === 'strong') return 'strong';
  if (s === 'medium') return 'medium';
  if (s === 'weak') return 'weak';
  if (s === 'absent') return 'missing';
  if (s === 'prompted') return 'prompted';
  return raw || '';
};

const renderStructuredFeedback = (data) => {
  if (!data) return null;
  const scores = data.scores;
  const verdictText = formatOverallVerdict(data.overall_verdict);
  const iso = formatIsomorphism(data.isomorphism_score);
  const evidenceOutline = Array.isArray(data.evidence_outline) ? data.evidence_outline : [];
  const hasScores = scores && typeof scores === 'object' && Object.keys(scores).length > 0;
  if (!hasScores && !verdictText && !iso && evidenceOutline.length === 0) return null;

  return (
    <div className="feedback-structured">
      {data.graph_feedback ? (
        <div className="feedback-graph-badge">Graph-driven feedback</div>
      ) : null}

      {verdictText ? (
        <div className="feedback-verdict-block">
          <span className="feedback-kicker">Overall verdict</span>
          <p className="feedback-verdict-text">{verdictText}</p>
        </div>
      ) : null}

      {iso ? (
        <div className="feedback-subsection">
          <h5 className="feedback-subsection-title">Reasoning coverage</h5>
          <p className="feedback-score-line">
            {iso.total != null ? (
              <>
                <span className="feedback-score-value">
                  {typeof iso.total === 'number' ? iso.total.toFixed(2) : iso.total}
                </span>
                {iso.band ? <span className="feedback-score-emdash"> — {iso.band}</span> : null}
              </>
            ) : null}
            {(iso.node_coverage != null || iso.edge_coverage != null || iso.reasoning_coverage != null) ? (
              <span className="feedback-score-emdash">
                {iso.total != null ? ' · ' : ''}
                {(iso.reasoning_coverage ?? iso.node_coverage) != null
                  ? `concept coverage ${iso.reasoning_coverage ?? iso.node_coverage}`
                  : ''}
                {(iso.reasoning_coverage ?? iso.node_coverage) != null &&
                (iso.connection_coverage ?? iso.edge_coverage) != null
                  ? ', '
                  : ''}
                {(iso.connection_coverage ?? iso.edge_coverage) != null
                  ? `connection coverage ${iso.connection_coverage ?? iso.edge_coverage}`
                  : ''}
              </span>
            ) : null}
          </p>
        </div>
      ) : null}

      {evidenceOutline.length > 0 ? (
        <div className="feedback-subsection">
          <h5 className="feedback-subsection-title">Evidence from your essay</h5>
          <ul className="feedback-bullet-list">
            {evidenceOutline.map((row, i) => {
              const label = humanizeConceptLabel(
                row.label || row.concept || row.primitive || row.node_id
              );
              const kind =
                row.graph === 'TG' || row.graph === 'Reasoning'
                  ? 'Reasoning'
                  : row.graph === 'LG' || row.graph === 'Knowledge'
                    ? 'Knowledge'
                    : row.graph || 'Concept';
              return (
                <li key={i}>
                  <strong>
                    {kind}
                    {label ? ` · ${label}` : ''}
                    {row.activation ? ` (${activationPlain(row.activation)})` : ''}
                  </strong>
                  {': '}
                  “{row.evidence_phrase}”
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {hasScores ? (
        <div className="feedback-scores-block">
          <span className="feedback-kicker">Scores (0–10)</span>
          {SCORE_DISPLAY_ORDER.map((key) => {
            const row = scores[key];
            if (!row) return null;
            const s = typeof row.score === 'number' ? row.score : parseInt(row.score, 10) || 0;
            const detail = row.comment || row.justification || '';
            return (
              <div key={key} className="feedback-score-row">
                <div className="feedback-score-heading">{SCORE_LABELS[key] || key}</div>
                <p className="feedback-score-line">
                  <span className="feedback-score-value">{s}/10</span>
                  {detail ? (
                    <>
                      <span className="feedback-score-emdash"> — </span>
                      <span>{detail}</span>
                    </>
                  ) : null}
                </p>
              </div>
            );
          })}
        </div>
      ) : null}

      {Array.isArray(data.major_flaws) && data.major_flaws.length > 0 ? (
        <div className="feedback-subsection">
          <h5 className="feedback-subsection-title">Major flaws</h5>
          <ul className="feedback-bullet-list">
            {data.major_flaws.map((item, i) => (
              <li key={i}>{typeof item === 'string' ? item : formatMissedItem(item)}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {Array.isArray(data.missed_from_case) && data.missed_from_case.length > 0 ? (
        <div className="feedback-subsection">
          <h5 className="feedback-subsection-title">What you missed from the case</h5>
          <ul className="feedback-bullet-list">
            {data.missed_from_case.map((item, i) => (
              <li key={i}>{formatMissedItem(item)}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {Array.isArray(data.how_to_fix) && data.how_to_fix.length > 0 ? (
        <div className="feedback-subsection">
          <h5 className="feedback-subsection-title">How to fix your answer</h5>
          <ul className="feedback-bullet-list">
            {data.how_to_fix.map((item, i) => (
              <li key={i}>{formatHowToFixItem(item)}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

const formatInlineBold = (str) => {
  if (!str) return null;
  const lines = str.split('\n');
  return lines.map((line, li) => (
    <React.Fragment key={`ln-${li}`}>
      {li > 0 ? <br /> : null}
      {line.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
        part.startsWith('**') && part.endsWith('**') ? (
          <strong key={`${li}-${i}`}>{part.slice(2, -2)}</strong>
        ) : (
          part
        )
      )}
    </React.Fragment>
  ));
};

const renderCaseStudyFeedback = (text) => {
  if (!text || !text.trim()) return null;
  const normalized = text.replace(/\r\n/g, '\n').trim();
  const hasStructuredHeadings = /(^|\n)##\s+/.test(normalized);
  if (!hasStructuredHeadings) {
    return (
      <div className="feedback-structured feedback-structured--plain">
        <p className="feedback-para">{normalized}</p>
      </div>
    );
  }

  const lines = normalized.split('\n');
  const out = [];
  let listItems = null;
  let paraBuffer = [];

  const flushPara = () => {
    if (paraBuffer.length) {
      const block = paraBuffer.join('\n').trim();
      if (block) {
        out.push(
          <p key={`p-${out.length}`} className="feedback-para">
            {formatInlineBold(block)}
          </p>
        );
      }
      paraBuffer = [];
    }
  };

  const flushList = () => {
    if (listItems && listItems.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="feedback-list">
          {listItems.map((item, idx) => (
            <li key={idx}>{formatInlineBold(item)}</li>
          ))}
        </ul>
      );
      listItems = null;
    }
  };

  for (let i = 0; i < lines.length; i += 1) {
    const trimmed = lines[i].trim();
    if (trimmed.startsWith('## ')) {
      flushList();
      flushPara();
      out.push(
        <h3 key={`h2-${i}`} className="feedback-block-title">
          {trimmed.slice(3).trim()}
        </h3>
      );
      continue;
    }
    if (trimmed.startsWith('### ')) {
      flushList();
      flushPara();
      out.push(
        <h4 key={`h3-${i}`} className="feedback-subtitle">
          {trimmed.slice(4).trim()}
        </h4>
      );
      continue;
    }
    if (/^[-*]\s+/.test(trimmed) || /^\d+\.\s+/.test(trimmed)) {
      flushPara();
      if (!listItems) listItems = [];
      listItems.push(trimmed.replace(/^[-*]\s+/, '').replace(/^\d+\.\s+/, ''));
      continue;
    }
    if (trimmed === '') {
      flushPara();
      continue;
    }
    flushList();
    paraBuffer.push(trimmed);
  }
  flushList();
  flushPara();

  return <div className="feedback-structured">{out}</div>;
};

const stripHtmlToText = (html) => html?.replace(/<[^>]*>/g, '').trim() || '';

const hasValidContent = (figure) => {
  const htmlOk =
    figure.is_HTML &&
    figure.HTML_data &&
    stripHtmlToText(figure.HTML_data).length > 10;
  const imageOk =
    figure.is_image &&
    figure.image_url &&
    String(figure.image_url).trim().length > 10;
  if (htmlOk || imageOk) return true;
  if (figure.description && stripHtmlToText(figure.description).length > 10) {
    return true;
  }
  if (figure.title && stripHtmlToText(figure.title).length > 10) {
    return true;
  }
  return false;
};

const figureHasDisplayableImage = (figure) =>
  figure &&
  figure.image_url &&
  String(figure.image_url).trim().length > 10 &&
  figure.is_image;

const figureHasDisplayableHtml = (figure) =>
  figure &&
  figure.is_HTML &&
  figure.HTML_data &&
  stripHtmlToText(figure.HTML_data).length > 0;

/** Law Exhibit 5 (Judicial Reasoning) often has trailing junk after the case
 *  analysis — hide it in the UI only. Live DB variants include:
 *  - "no dissent."
 *  - "Dissent: none." then a leaked "<h1>Section A: Contract formation..."
 */
const truncateLawExhibit5Html = (html) => {
  if (!html || typeof html !== 'string') return html;
  let out = html;

  // Drop leaked syllabus / appendix heading and everything after it
  const sectionAMatch = out.match(/<h1[^>]*>\s*Section\s+A\s*:/i)
    || out.match(/Section\s+A\s*:\s*Contract\s+formation/i);
  if (sectionAMatch && typeof sectionAMatch.index === 'number') {
    out = out.slice(0, sectionAMatch.index);
  }

  const lower = out.toLowerCase();
  const markers = ['no dissent.', 'dissent: none.', 'dissent:</strong> none.'];
  let cutEnd = -1;
  for (const marker of markers) {
    const idx = lower.indexOf(marker);
    if (idx !== -1) {
      const end = idx + marker.length;
      if (cutEnd === -1 || end < cutEnd) cutEnd = end;
    }
  }
  if (cutEnd !== -1) {
    out = out.slice(0, cutEnd);
  }

  return out;
};

const isLawCaseSubject = (subject) =>
  String(subject || '').toLowerCase().includes('law');

const isLawExhibit5 = (exhibit, exhibitIndex, subject) => {
  if (!isLawCaseSubject(subject)) return false;
  if (exhibitIndex === 4) return true;
  const title = stripHtmlToText(exhibit?.title || '').toLowerCase();
  return title.includes('judicial reasoning');
};

const getExhibitHtmlForDisplay = (exhibit, exhibitIndex, subject) => {
  const html = exhibit?.HTML_data;
  if (!html) return html;
  if (!isLawExhibit5(exhibit, exhibitIndex, subject)) return html;
  return truncateLawExhibit5Html(html);
};

// Static icon lookup by section "type" — genuinely static (unlike the section
// list itself, which depends on figures.length and must stay component-scoped).
// Single-stroke, no fill, sized for a 16x16 slot beside a label.
const SECTION_ICONS = {
  film: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="1.5" />
      <path d="M7 4v16M17 4v16M3 9h4M17 9h4M3 15h4M17 15h4" />
    </svg>
  ),
  act: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19V6a1 1 0 0 1 1-1h9l6 6v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" />
      <path d="M14 5v5a1 1 0 0 0 1 1h5" />
    </svg>
  ),
  exhibit: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="3" width="14" height="18" rx="1.5" />
      <path d="M9 8h6M9 12h6M9 16h3" />
    </svg>
  ),
  decision: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3l3 3-6 6 3 3 6-6 3 3" />
      <path d="M13 6l7 7" />
      <path d="M3 21h9" />
    </svg>
  ),
  quiz: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 1 1 3.6 2.2c-.7.4-1.1.9-1.1 1.8" />
      <path d="M12 17h.01" />
    </svg>
  ),
  write: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 20l1-4 12-12 3 3-12 12-4 1z" />
      <path d="M14 5l3 3" />
    </svg>
  ),
  tutorial: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H12v18H6.5A2.5 2.5 0 0 1 4 18.5v-13z" />
      <path d="M20 5.5A2.5 2.5 0 0 0 17.5 3H12v18h5.5A2.5 2.5 0 0 0 20 18.5v-13z" />
    </svg>
  ),
  discussion: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a8 8 0 0 1-8 8H6l-3 3 .6-4.2A8 8 0 1 1 21 12z" />
    </svg>
  ),
};

const CaseStudy = ({ embeddedId, initialCaseStudy, onComplete }) => {
  const { id: routeId } = useParams();
  const id = embeddedId || routeId;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [caseStudy, setCaseStudy] = useState(null);
  const [figures, setFigures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
    const [searchParams] = useSearchParams();
  const initialSection = (() => {
    const raw = parseInt(searchParams.get('section'), 10);
    return Number.isInteger(raw) && raw >= 0 ? raw : 0;
  })();
  const [currentSection, setCurrentSection] = useState(initialSection);
  // 0: Film, 1: Act I, 2: Act II, 3: Act III,
  // 4+: Exhibits, then Decision, Writing Task, Act IV, QUIZ, Oxford Tutorial, Harvard Discussion
  const [sectionTransitionPhase, setSectionTransitionPhase] = useState('idle');
  const [isMainTransitioning, setIsMainTransitioning] = useState(false);
  const [watchFilmToastVisible, setWatchFilmToastVisible] = useState(false);
  const watchFilmToastTimeoutRef = useRef(null);
  const [currentAct, setCurrentAct] = useState(0); // Which act is selected in acts section
  const [selectedExhibit, setSelectedExhibit] = useState(null);
  const [exhibitModalOpen, setExhibitModalOpen] = useState(false);
  /** Full-screen diagram viewer opened from exhibit "Diagrams" thumbnails */
  const [diagramViewer, setDiagramViewer] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [decision, setDecision] = useState('');
  const [submittingDecision, setSubmittingDecision] = useState(false);
  const [figuresLoading, setFiguresLoading] = useState(true);
  const exhibitStartSection = 4;
  const decisionSection = exhibitStartSection + figures.length;
  const writingTaskSection = decisionSection + 1;
  const actIVSection = writingTaskSection + 1;
  const quizSection = actIVSection + 1;
  const oxfordTutorialSection = quizSection + 1;
  const harvardDiscussionSection = oxfordTutorialSection + 1;
  const totalSections = harvardDiscussionSection + 1;
  const maxSection = totalSections - 1;
  console.log('SECTION NUMBERS:', {
    figures: figures.length,
    exhibitStartSection,
    decisionSection,
    writingTaskSection,
    actIVSection,
    quizSection,
    oxfordTutorialSection,
    harvardDiscussionSection,
    maxSection
  });

  // Rail sections: must stay component-scoped (not a module-level constant),
  // since it depends on figures.length which varies per case study. Mirrors
  // the section-index math above (exhibitStartSection..harvardDiscussionSection).
  const railSections = useMemo(() => {
    const isLockedIndex = (index) => false;
    const list = [
      { label: 'Film', icon: 'film' },
      { label: 'Act I', icon: 'act' },
      { label: 'Act II', icon: 'act' },
      { label: 'Act III', icon: 'act' },
      ...figures.map((_, i) => ({ label: `Exhibit ${i + 1}`, icon: 'exhibit' })),
      { label: 'Decision', icon: 'decision' },
      { label: 'Writing Task', icon: 'write' },
      { label: 'Act IV', icon: 'act' },
      { label: 'QUIZ', icon: 'quiz' },
      { label: 'Oxford Tutorial', icon: 'tutorial' },
      { label: 'Harvard Case Method', icon: 'discussion' },
    ];
    return list.map((entry, index) => ({
      ...entry,
      index,
      locked: isLockedIndex(index),
    }));
  }, [figures]);
  
  // Feedback-related state
  const [feedback, setFeedback] = useState(null);
  const [feedbackStructured, setFeedbackStructured] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackLoadingMessage, setFeedbackLoadingMessage] = useState('Generating personalized feedback...');
  const [showFeedback, setShowFeedback] = useState(false);
  const [tourTutorialHint, setTourTutorialHint] = useState(false);
  const [quizCounts, setQuizCounts] = useState({ reasoning: 0, knowledge: 0, total: 0 });

  const currentSectionRef = useRef(currentSection);
  const caseSessionStartedAtRef = useRef(null);
  const activeSegmentStartedAtRef = useRef(null);
  const accumulatedActiveMsRef = useRef(0);
  const activityFinalizedRef = useRef(false);
  const currentActRef = useRef(currentAct);
  const selectedOptionRef = useRef(selectedOption);
  const decisionRef = useRef(decision);
  const decisionEditorRef = useRef(null);
  const railFillRef = useRef(null);
  const railItemRefs = useRef([]);
  const optionCardRefs = useRef([]);
  const railListRef = useRef(null);
  const mainContentRef = useRef(null);
  const [railScrollable, setRailScrollable] = useState(false);
  const tutorialPreloadRef = useRef(null);
  const discussionPreloadRef = useRef(null);
  useEffect(() => {
    document.documentElement.classList.add('case-study-page-active');
    document.body.classList.add('case-study-page-active');
    return () => {
      document.documentElement.classList.remove('case-study-page-active');
      document.body.classList.remove('case-study-page-active');
    };
  }, []);
  useEffect(() => {
    currentSectionRef.current = currentSection;
  }, [currentSection]);
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set('section', String(currentSection));

    navigate(
      `/case-study/${id}?${params.toString()}`,
      { replace: true }
    );
  }, [currentSection, id, navigate, searchParams]);
  useEffect(() => {
    currentActRef.current = currentAct;
  }, [currentAct]);
  useEffect(() => {
    selectedOptionRef.current = selectedOption;
  }, [selectedOption]);
  useEffect(() => {
    decisionRef.current = decision;
  }, [decision]);

  // Deep links / quizzes use ?focus=tutorial|discussion|quiz so section index
  // resolves after exhibit count is known (scales with any figures.length).
  useEffect(() => {
    if (figuresLoading) return;
    const focus = (searchParams.get('focus') || '').toLowerCase();
    if (!focus) return;

    let target = null;
    if (focus === 'tutorial' || focus === 'oxford') {
      target = oxfordTutorialSection;
    } else if (focus === 'discussion' || focus === 'harvard') {
      target = harvardDiscussionSection;
    } else if (focus === 'quiz' || focus === 'quizzes') {
      target = quizSection;
    }
    if (target == null) return;

    setCurrentSection(target);
    const params = new URLSearchParams(searchParams);
    params.delete('focus');
    params.set('section', String(target));
    navigate(`/case-study/${id}?${params.toString()}`, { replace: true });
  }, [
    figuresLoading,
    searchParams,
    quizSection,
    oxfordTutorialSection,
    harvardDiscussionSection,
    id,
    navigate,
  ]);

  useEffect(() => {
    const onTourAction = (event) => {
      const detail = event.detail || {};
      const {
        caseStudySection,
        setCaseAct,
        closeCasePanel,
        openCasePanel,
        openFirstExhibit,
        closeExhibitModal,
        showTutorialHint,
        tourPreviewDecision,
      } = detail;

      if (closeExhibitModal) {
        setExhibitModalOpen(false);
        setSelectedExhibit(null);
      }
      if (showTutorialHint === true) setTourTutorialHint(true);
      if (showTutorialHint === false) setTourTutorialHint(false);

      if (caseStudySection !== undefined && caseStudySection !== null) {
        setCurrentSection(caseStudySection);
      }
      if (setCaseAct !== undefined && setCaseAct !== null) {
        setCurrentAct(setCaseAct);
      }

      if (openFirstExhibit && figures.length > 0) {
        setCurrentSection(exhibitStartSection);
        setTimeout(() => openExhibitModal(figures[0]), 300);
      }

      if (tourPreviewDecision && caseStudy) {
        setCurrentSection(decisionSection);
        setShowFeedback(false);
        const opts = parseStrategicOptions(caseStudy.strategic_options);
        if (opts.length > 0 && selectedOptionRef.current === null) {
          setSelectedOption(0);
        }
      }
    };
    window.addEventListener('pathbuddy-tour-action', onTourAction);
    return () => window.removeEventListener('pathbuddy-tour-action', onTourAction);
  }, [figures, caseStudy]);

  // Start timing as soon as the case page is opened for this case ID.
  useEffect(() => {
    if (id) {
      const now = Date.now();
      caseSessionStartedAtRef.current = now;
      activeSegmentStartedAtRef.current = now;
      accumulatedActiveMsRef.current = 0;
      activityFinalizedRef.current = false;
    }
  }, [id]);

  // Pause timer when tab is hidden; resume when visible.
  useEffect(() => {
    const handleVisibilityChange = () => {
      const now = Date.now();
      if (document.visibilityState === 'hidden') {
        if (activeSegmentStartedAtRef.current) {
          accumulatedActiveMsRef.current += now - activeSegmentStartedAtRef.current;
          activeSegmentStartedAtRef.current = null;
        }
      } else if (document.visibilityState === 'visible') {
        if (!activeSegmentStartedAtRef.current && caseSessionStartedAtRef.current) {
          activeSegmentStartedAtRef.current = now;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Get user info from auth context
  const userName = user?.name || 'User';
  const userEmail = user?.email || 'user@pathbuddy.com';

  const buildActivityPayload = (eventType) => {
    const nowMs = Date.now();
    const startedAtMs = caseSessionStartedAtRef.current || nowMs;
    let activeMs = accumulatedActiveMsRef.current;
    if (activeSegmentStartedAtRef.current) {
      activeMs += nowMs - activeSegmentStartedAtRef.current;
    }

    return {
      activity: {
        started_at: new Date(startedAtMs).toISOString(),
        submitted_at: new Date(nowMs).toISOString(),
        time_spent_seconds: Math.max(0, Math.floor(activeMs / 1000)),
        section_at_submit: currentSectionRef.current,
        current_act_at_submit: currentActRef.current,
        event_type: eventType,
      },
      selected_option: selectedOptionRef.current,
      reasoning: decisionRef.current || '',
    };
  };

  const persistAbandonActivity = () => {
    if (!id || activityFinalizedRef.current || !caseSessionStartedAtRef.current) {
      return;
    }

    const payload = buildActivityPayload('abandoned');
    const token =
      localStorage.getItem('pathbuddy_access_token') ||
      sessionStorage.getItem('pathbuddy_access_token');
    if (!token) return;

    activityFinalizedRef.current = true;

    try {
      fetch(`${API_CONFIG.baseURL}/api/case-study/${id}/activity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    } catch (error) {
      // best-effort only for unload/exit activity persistence
      console.warn('Case activity flush failed:', error);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const processFigures = (rawFigures) => {
      if (!Array.isArray(rawFigures) || rawFigures.length === 0) return [];
      const transformedFigures = rawFigures.map((figure) => {
        const nonEmptyDescription =
          typeof figure.description === 'string' && figure.description.trim().length > 0
            ? figure.description
            : '';
        const isImage = figure.is_image || false;
        const isHtml = figure.is_HTML || false;
        const hasImg =
          isImage &&
          figure.image_url &&
          String(figure.image_url).trim().length > 10;
        return {
          id: figure.id,
          title: figure.title || 'Exhibit',
          description: nonEmptyDescription,
          notes: figure.notes,
          source: figure.source,
          is_HTML: isHtml,
          HTML_data: figure.HTML_data,
          is_image: isImage,
          image_url: figure.image_url,
          type: hasImg ? 'chart' : isHtml ? 'analysis' : 'document',
        };
      });
      return transformedFigures.filter(hasValidContent);
    };

    if (initialCaseStudy) {
      setCaseStudy(initialCaseStudy);
      if (initialCaseStudy.figures) {
        setFigures(processFigures(initialCaseStudy.figures));
        setFiguresLoading(false);
      } else if (id) {
        setFiguresLoading(true);
        caseStudyAPI.getCaseStudyFigures(id)
          .then((res) => {
            if (isMounted) {
              setFigures(processFigures(res));
            }
          })
          .catch((err) => {
            console.warn('Could not fetch figures:', err);
            if (isMounted) setFigures([]);
          })
          .finally(() => {
            if (isMounted) setFiguresLoading(false);
          });
      } else {
        setFigures([]);
        setFiguresLoading(false);
      }
      setLoading(false);
      return () => { isMounted = false; };
    }

    const fetchCaseStudy = async () => {
      try {
        setLoading(true);
        setFiguresLoading(true);
        const data = await caseStudyAPI.getCaseStudyById(id);
        if (!isMounted) return;
        setCaseStudy(data);

        caseStudyAPI.getCaseQuizCounts(id).then((counts) => {
          if (isMounted && counts && typeof counts.total === 'number') setQuizCounts(counts);
        }).catch(() => {});
        
        // Fetch figures/exhibits from database for this specific case study
        if (data.figures && Array.isArray(data.figures) && data.figures.length > 0) {
          setFigures(processFigures(data.figures));
          setFiguresLoading(false);
        } else {
          try {
            const figuresResponse = await caseStudyAPI.getCaseStudyFigures(id);
            if (isMounted) {
              const validFigs = processFigures(figuresResponse);
              setFigures(validFigs);
            }
          } catch (figuresError) {
            console.warn('Could not fetch figures from database:', figuresError);
            if (isMounted) setFigures([]);
          } finally {
            if (isMounted) setFiguresLoading(false);
          }
        }
      } catch (err) {
        console.error('Error fetching case study:', err);
        if (isMounted) {
          setError('Could not load this case study. Please try again.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
          setFiguresLoading(false);
        }
      }
    };

    if (id) {
      fetchCaseStudy();
    } else {
      setLoading(false);
      setFiguresLoading(false);
    }

    return () => { isMounted = false; };
  }, [id, initialCaseStudy]);

  useEffect(() => {
    if (!diagramViewer) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setDiagramViewer(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [diagramViewer]);

  // Rail "fill" line: grows to the active item's position. Driven from
  // currentSection state (not IntersectionObserver, unlike the reference demo)
  // since sections are swapped in/out of the DOM one at a time, not scrolled
  // through as one continuous document.
  useEffect(() => {
    const activeItem = railItemRefs.current[currentSection];
    const fillEl = railFillRef.current;
    const listEl = railListRef.current;
    if (!activeItem || !fillEl || !listEl) return;

    const itemTop = activeItem.offsetTop - listEl.scrollTop;
    const itemMid = itemTop + activeItem.offsetHeight / 2;
    fillEl.style.height = `${Math.max(itemMid, 0)}px`;
  }, [currentSection, railSections.length]);

  // Rail scroll cap only when the list actually overflows it — short lists
  // render fully unclipped (so the hover tooltip isn't cut off), long
  // exhibit lists still get the scrollable cap. max-height stays applied
  // via CSS regardless of overflow, so clientHeight is already the capped
  // height here; no need to duplicate the CSS calc() formula in JS.
  useLayoutEffect(() => {
    const measure = () => {
      const el = railListRef.current;
      if (!el) return;
      setRailScrollable(el.scrollHeight > el.clientHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [currentSection, railSections.length]);

  // Equalize strategic-option-card heights to the tallest card, so a short
  // option in one row doesn't look mismatched next to a long one in another.
  // Skipped on mobile, where .options-list collapses to a single column
  // (CaseStudy.css:912) and matching heights isn't meaningful. Depends on
  // caseStudy?.strategic_options (not the parsed strategicOptions variable,
  // computed later below the loading/error early-returns) so this hook can
  // sit alongside the component's other unconditional hooks.
  useLayoutEffect(() => {
    const equalize = () => {
      const cards = optionCardRefs.current.filter(Boolean);
      if (!cards.length) return;
      cards.forEach((card) => { card.style.height = ''; });
      if (window.innerWidth <= 768) return;
      const maxHeight = Math.max(...cards.map((card) => card.offsetHeight));
      cards.forEach((card) => { card.style.height = `${maxHeight}px`; });
    };
    equalize();
    window.addEventListener('resize', equalize);
    return () => window.removeEventListener('resize', equalize);
  }, [caseStudy?.strategic_options, currentSection]);

// The rail unmounts on Film (currentSection === 0). If a rail button held
  // focus when that happened, the browser drops focus to <body> — redirect
  // it to the main content so Tab order doesn't silently restart from the
  // top of the page.
  useEffect(() => {
    if (currentSection === 0 && document.activeElement === document.body) {
      mainContentRef.current?.focus();
    }
  }, [currentSection]);

  // Section changes should start at the top, not wherever the previous
  // section happened to be scrolled to. useLayoutEffect (not useEffect) so
  // this resolves before paint — no flash of the new section at the old
  // scroll offset. Scoped to mainContentRef's own subtree, which naturally
  // excludes the rail's scrollable list and any open modal/diagram viewer
  // (both are siblings of <main>, not descendants), so neither has its
  // scroll position reset by this.
  useLayoutEffect(() => {
    const el = mainContentRef.current;
    if (!el) return;
    el.scrollTop = 0;
    document.documentElement.style.scrollBehavior = 'auto';
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0; // Safari sometimes delegates window scroll to body instead of <html>
    document.documentElement.style.scrollBehavior = ''; // restore, don't leave it forced off
    el.querySelectorAll('.natively-scrollable').forEach((child) => {
      child.scrollTop = 0;
    });

    // Hard-stop any in-progress momentum/inertial scroll from the gesture
    // that triggered this section change — a plain scrollTop=0 isn't
    // enough on touch devices, since native inertial scrolling keeps
    // running after touchend and can carry the position away again right
    // after we reset it (confirmed via screen recording: sections were
    // opening mid-content, not at the top). Runs on every device, not just
    // touch — a trackpad/wheel gesture that pages to a new section
    // generates the same kind of residual momentum as a touch swipe, and
    // pointer:fine doesn't exclude trackpads.
    //
    // Actively correcting on every scroll event, rather than freezing via
    // overflow-y for a fixed duration: a fixed freeze window can't tell
    // whether momentum has actually decayed yet, so it either cuts off too
    // early (drift resumes once unfrozen) or holds too long. Instead, snap
    // back to 0 on every scroll event the still-decelerating momentum
    // generates, and only stop once a short idle gap (no further scroll
    // events) shows momentum has genuinely settled — with an absolute
    // ceiling so a pathological case can't pin the listener open forever.
    // Trade-off: a deliberate second-finger scroll (touch) or continuous
    // wheel-spinning (desktop) during this window gets suppressed too, and
    // the container will likely show a brief flicker as each momentum tick
    // paints for a frame before being snapped back (no overflow freeze this
    // time to prevent that intermediate paint) — acceptable given the
    // alternative is landing mid-content and staying there.
    //
    // Capture phase (not bubble): `scroll` events don't bubble, but they do
    // still propagate through the capture phase from window down to the
    // actual target, so this also catches momentum inside a nested
    // .natively-scrollable container (e.g. .act-text-content), not just
    // window-level drift. That inner-target reset is scoped to
    // mainContentRef.current's subtree — matching the same boundary the
    // effect's own initial reset above already respects — so it can never
    // touch the rail nav list (a sibling of <main>, not a descendant, which
    // carries .natively-scrollable and genuinely scrolls independently on
    // cases with enough sections to overflow it).
    if (typeof window !== 'undefined') {
      let idleTimer = null;
      let hardStop = null;
      const finish = () => {
        window.removeEventListener('scroll', onScroll, true);
        clearTimeout(idleTimer);
        clearTimeout(hardStop);
      };
      const onScroll = (e) => {
        document.documentElement.style.scrollBehavior = 'auto';
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        document.documentElement.style.scrollBehavior = '';
        if (
          e.target &&
          e.target.scrollTop !== undefined &&
          e.target !== document &&
          mainContentRef.current?.contains(e.target)
        ) {
          e.target.scrollTop = 0;
        }
        clearTimeout(idleTimer);
        idleTimer = setTimeout(finish, 100);
      };
      window.addEventListener('scroll', onScroll, { passive: true, capture: true });
      idleTimer = setTimeout(finish, 100);
      hardStop = setTimeout(finish, 800);
      return finish;
    }
  }, [currentSection]);

  // Prefetch tutorial/discussion data one section ahead so the panel shows
  // content immediately instead of a "Loading…" screen. Mirrors boot()'s own
  // branching exactly (skip startCaseTutorialAttempt when there are no
  // stored questions) so the prefetch never creates a side effect the direct
  // fetch path wouldn't also create.
  useEffect(() => {
    if (currentSection !== writingTaskSection) return;
    const promise = caseStudyAPI.getCaseTutorials(id).then((payload) => {
      const qs = payload.questions || [];
      if (!qs.length) return { payload, attempt: null };
      return caseStudyAPI.startCaseTutorialAttempt(id).then((attempt) => ({ payload, attempt }));
    });
    promise.catch(() => {}); // avoid a console "unhandled rejection" if the user never
                             // reaches the tutorial section to consume this promise
    tutorialPreloadRef.current = { caseStudyId: id, promise };
  }, [currentSection, id, writingTaskSection]);

  useEffect(() => {
    if (currentSection !== oxfordTutorialSection) return;
    const promise = caseStudyAPI.getCaseDiscussion(id);
    promise.catch(() => {});
    discussionPreloadRef.current = { caseStudyId: id, promise };
  }, [currentSection, id, oxfordTutorialSection]);

  // Section navigation via wheel: deliberate gestures only, cooldown after each jump,
  // and native scrolling preserved inside scrollable regions (decision, acts, exhibits, modals, inputs).
  useEffect(() => {
    const accumulatedRef = { magnitude: 0, sign: 0 };
    let lastWheelSectionChange = 0;

    const mountedAt = Date.now();
    const SETTLE_MS = SECTION_PAGE_SETTLE_MS;

    const COOLDOWN_MS = SECTION_PAGE_COOLDOWN_MS;
    const DELTA_THRESHOLD = 360;

    const normalizeWheelDeltaY = (e) => {
      if (e.deltaMode === 1) return e.deltaY * 16;
      if (e.deltaMode === 2) return e.deltaY * 800;
      return e.deltaY;
    };

    const handleWheel = (e) => {
      if (Date.now() - mountedAt < SETTLE_MS) return;
      const deltaY = normalizeWheelDeltaY(e);
      if (deltaY === 0) return;

      const target = e.target;

      if (target.closest('textarea, input, select')) {
        return;
      }

      if (
        target.closest('.exhibit-modal-backdrop') ||
        target.closest('.exhibit-modal') ||
        target.closest('.diagram-viewer-backdrop') ||
        target.closest('.diagram-viewer')
      ) {
        if (isScrollableAncestorConsumingWheel(target, deltaY)) return;
        e.preventDefault();
        return;
      }

      if (isScrollableAncestorConsumingWheel(target, deltaY)) {
        return;
      }

      const now = Date.now();
      if (now - lastWheelSectionChange < COOLDOWN_MS) {
        e.preventDefault();
        return;
      }

      const sign = Math.sign(deltaY);
      if (accumulatedRef.sign !== 0 && sign !== accumulatedRef.sign) {
        accumulatedRef.magnitude = 0;
      }
      accumulatedRef.sign = sign;
      accumulatedRef.magnitude += Math.abs(deltaY);

      if (accumulatedRef.magnitude < DELTA_THRESHOLD) {
        e.preventDefault();
        return;
      }

      accumulatedRef.magnitude = 0;
      accumulatedRef.sign = 0;

      const prev = currentSectionRef.current;
      let next = prev;

      if (sign > 0 && prev < maxSection) next = prev + 1;
      else if (sign < 0 && prev > 0) next = prev - 1;

      if (next !== prev) {
        lastWheelSectionChange = now;

        console.log('WHEEL CHANGE:', {
          prev,
          next,
          sign,
          maxSection
        });

        setCurrentSection(next);
      }

      e.preventDefault();
    };

    window.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, [maxSection]);

  // Section navigation via touch swipe: mirrors the wheel handler's scroll-vs-page
  // disambiguation above exactly (same thresholds, same scrollable-ancestor check,
  // same decision-lock/bounds logic), as a second, independent input path. Only
  // touchstart/touchend are used — no touchmove, no preventDefault — so an
  // in-progress native scroll is never interrupted, and there's no iOS scroll-jank
  // risk from a non-passive touchmove listener.
  useEffect(() => {
    const mountedAt = Date.now();
    let lastTouchSectionChange = 0;

    let touchStartY = 0;
    let touchStartX = 0;
    let touchStartTarget = null;
    let isMultiTouch = false;

    const onTouchStart = (e) => {
      if (e.touches.length > 1) {
        isMultiTouch = true;
        return;
      }
      isMultiTouch = false;
      touchStartY = e.touches[0].clientY;
      touchStartX = e.touches[0].clientX;
      touchStartTarget = e.target;
    };

    const onTouchEnd = (e) => {
      if (isMultiTouch) return;
      if (Date.now() - mountedAt < SECTION_PAGE_SETTLE_MS) return;
      if (!touchStartTarget) return;

      const touch = e.changedTouches[0];
      if (!touch) return;

      const deltaY = touchStartY - touch.clientY;
      const deltaX = touchStartX - touch.clientX;

      if (touchStartTarget.closest('textarea, input, select')) return;
      if (Math.abs(deltaX) > Math.abs(deltaY)) return;
      if (Math.abs(deltaY) < SWIPE_MIN_DISTANCE_PX) return;

      // Mirrors handleWheel's unconditional modal/diagram-viewer block: being
      // inside either overlay always discards, regardless of whether its own
      // content has room to scroll.
      if (
        touchStartTarget.closest(
          '.exhibit-modal-backdrop, .exhibit-modal, .diagram-viewer-backdrop, .diagram-viewer'
        )
      ) {
        return;
      }

      if (isScrollableAncestorConsumingWheel(touchStartTarget, deltaY)) return;

      const now = Date.now();
      if (now - lastTouchSectionChange < SECTION_PAGE_COOLDOWN_MS) return;

      const sign = Math.sign(deltaY);
      const prev = currentSectionRef.current;
      let next = prev;

      if (sign > 0 && prev < maxSection) next = prev + 1;
      else if (sign < 0 && prev > 0) next = prev - 1;

      if (next !== prev) {
        lastTouchSectionChange = now;
        setCurrentSection(next);
      }
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [maxSection]);

  // Persist abandoned session activity on close/navigation away.
  useEffect(() => {
    const handlePageHide = () => {
      persistAbandonActivity();
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handlePageHide);
      persistAbandonActivity();
    };
  }, [id]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown' && currentSection < maxSection) {
        setCurrentSection(prev => prev + 1);
      } else if (e.key === 'ArrowUp' && currentSection > 0) {
        setCurrentSection(prev => prev - 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentSection]);

  // Always leaves the case study directly, from any section — no
  // per-section stepping (deliberately not "one level at a time").
  // Wired through ProductBackProvider so the shared floating back works.
  const handleBack = useCallback(() => {
    navigate(embeddedId ? '/apps' : '/cases');
  }, [embeddedId, navigate]);

  useProductBackHandler(handleBack);

  const handleReadCase = () => {
    setSectionTransitionPhase('exiting');
    setIsMainTransitioning(true);
    setTimeout(() => {
      setCurrentSection(1); // Act I
      setSectionTransitionPhase('entering');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setSectionTransitionPhase('idle'));
      });
      setTimeout(() => setIsMainTransitioning(false), 320);
    }, 320);
  };

  const handleWatchFilm = () => {
    setWatchFilmToastVisible(true);
    if (watchFilmToastTimeoutRef.current) clearTimeout(watchFilmToastTimeoutRef.current);
    watchFilmToastTimeoutRef.current = setTimeout(() => setWatchFilmToastVisible(false), 2200);
  };

  const handleDecisionSubmit = async (e) => {
    e.preventDefault();
    if (!decision.trim()) return;

    setSubmittingDecision(true);
    setFeedbackLoading(true);
    setFeedbackLoadingMessage('Generating personalized feedback...');
    setFeedback(null);
    setFeedbackStructured(null);
    setShowFeedback(true);

    try {
      activityFinalizedRef.current = true;
      const submittedAtMs = Date.now();
      const startedAtMs = caseSessionStartedAtRef.current || submittedAtMs;
      let activeMs = accumulatedActiveMsRef.current;
      if (activeSegmentStartedAtRef.current) {
        activeMs += submittedAtMs - activeSegmentStartedAtRef.current;
      }
      const timeSpentSeconds = Math.max(0, Math.floor(activeMs / 1000));

      const payload = {
        reasoning: decision,
        selected_option: selectedOption,
        activity: {
          started_at: new Date(startedAtMs).toISOString(),
          submitted_at: new Date(submittedAtMs).toISOString(),
          time_spent_seconds: timeSpentSeconds,
          section_at_submit: currentSectionRef.current,
          current_act_at_submit: currentAct,
        },
      };

      let feedbackData;
      const preferGraph =
        caseStudy?.graph_driven === true || caseStudy?.graph_driven === 'true';

      if (preferGraph) {
        feedbackData = await caseStudyAPI.submitGraphCaseFeedback(id, payload, {
          onProgress: setFeedbackLoadingMessage,
        });
      } else {
        // Try graph path first; fall back to legacy if case is not graph-driven.
        try {
          feedbackData = await caseStudyAPI.submitGraphCaseFeedback(id, payload, {
            onProgress: setFeedbackLoadingMessage,
          });
        } catch (graphErr) {
          const notGraph =
            graphErr instanceof APIError &&
            graphErr.status === 400 &&
            /does not use graph-driven/i.test(
              String(graphErr.data?.error || graphErr.message || '')
            );
          if (!notGraph) throw graphErr;
          setFeedbackLoadingMessage('Generating personalized feedback...');
          feedbackData = await caseStudyAPI.submitCaseFeedback(id, payload);
        }
      }

      const structured = feedbackData.feedback_structured
        ? {
            ...feedbackData.feedback_structured,
            graph_feedback: Boolean(feedbackData.graph_feedback),
            isomorphism_score:
              feedbackData.isomorphism_score ??
              feedbackData.feedback_structured?.isomorphism_score,
            evidence_outline:
              feedbackData.evidence_outline ||
              feedbackData.feedback_structured?.evidence_outline ||
              [],
          }
        : null;

      setFeedback(feedbackData.feedback);
      setFeedbackStructured(structured);
    } catch (error) {
      console.error('Error getting feedback:', error);
      activityFinalizedRef.current = false;
      const isNetworkFailure =
        error?.name === 'AbortError' ||
        (error instanceof TypeError && /failed to fetch/i.test(error.message || ''));
      let fallback;
      if (isNetworkFailure) {
        fallback =
          'A graph feedback step timed out in the browser. ' +
          'This often means the LLM call is still slow — please try Submit again ' +
          '(completed steps are saved and will resume).';
      } else if (error instanceof APIError && error.status === 401) {
        fallback = 'Please sign in again to load AI feedback.';
        navigate('/login', { state: { from: { pathname: `/case-study/${id}` } }, replace: true });
      } else if (error instanceof APIError && (error.data?.detail || error.data?.error)) {
        fallback = error.data.detail || error.data.error;
      } else {
        fallback = generateClientFallbackFeedback(decision);
        if (error instanceof APIError && error.message) {
          console.warn('Feedback API:', error.message, error.data);
        }
      }
      setFeedback(fallback);
      setFeedbackStructured(null);
    } finally {
      setSubmittingDecision(false);
      setFeedbackLoading(false);
      setFeedbackLoadingMessage('Generating personalized feedback...');
    }
  };

  const handleNewAttempt = () => {
    setDecision('');
    setSelectedOption(null);
    setFeedback(null);
    setFeedbackStructured(null);
    setShowFeedback(false);
  };

  const handleStartCaseInterview = (assessment = 'tutorial') => {
    if (assessment === 'tutorial') {
      setCurrentSection(oxfordTutorialSection);
      return;
    }
    setCurrentSection(harvardDiscussionSection);
  };

  const handleStartCaseQuizzes = () => {
    setCurrentSection(quizSection);
  };

  const openExhibitModal = (exhibit) => {
    setDiagramViewer(null);
    setSelectedExhibit(exhibit);
    setExhibitModalOpen(true);
  };

  const closeExhibitModal = () => {
    setExhibitModalOpen(false);
    setSelectedExhibit(null);
    setDiagramViewer(null);
  };

  const closeDiagramViewer = () => setDiagramViewer(null);

  const openDiagramFromExhibit = (url, title) => {
    const u = url && String(url).trim();
    if (!u) return;
    setDiagramViewer({ url: u, title: title || 'Diagram' });
  };

  const cleanActContent = (content) => {
    if (!content) return '';

    return String(content)
      .replace(/^#{1,6}\s*Act\s*(?:[1-4]|[IVX]+)\s*$/gim, '')
      .replace(/^#{1,6}\s*Act\s*(?:[1-4]|[IVX]+)\s*:.*$/gim, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  // Helper function to safely render HTML content
  const renderHTMLContent = (htmlContent) => {
    if (!htmlContent) return null;

    // Audit #18: strip script/handlers only — keep case structure/styling tags.
    const sanitizedHTML = stripDangerousHtml(htmlContent);

    return (
      <div 
        className="html-content"
        dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
      />
    );
  };

  // Helper function to strip HTML tags for display text
  const stripHTMLTags = (htmlString) => {
    if (!htmlString) return '';
    return htmlString.replace(/<[^>]*>/g, '').trim();
  };

  // FIXED: Parse strategic options more intelligently
  const parseStrategicOptions = (strategicOptionsData) => {
    if (!strategicOptionsData) return [];
    
    let optionsArray = [];
    
    try {
      // Try to parse as JSON first
      if (typeof strategicOptionsData === 'string') {
        try {
          optionsArray = JSON.parse(strategicOptionsData);
        } catch (jsonError) {
          // If JSON parsing fails, try to parse as HTML or text with <br> tags
          if (strategicOptionsData.includes('<br>') || strategicOptionsData.includes('<BR>')) {
            // Split by <br> tags and clean up
            optionsArray = strategicOptionsData
              .split(/<br\s*\/?>/gi)
              .map(option => option.trim())
              .filter(option => option.length > 0)
              .map(option => {
                // Remove HTML tags from individual options
                return option.replace(/<[^>]*>/g, '').trim();
              })
              .filter(option => option.length > 0); // Filter out empty options after cleanup
          } else {
            // Try splitting by common delimiters
            const delimiters = ['\n', '|', ';'];
            for (const delimiter of delimiters) {
              if (strategicOptionsData.includes(delimiter)) {
                optionsArray = strategicOptionsData
                  .split(delimiter)
                  .map(option => option.trim())
                  .filter(option => option.length > 0);
                break;
              }
            }
            
            // If no delimiters found, treat as single option
            if (optionsArray.length === 0 && strategicOptionsData.trim()) {
              optionsArray = [strategicOptionsData.trim()];
            }
          }
        }
      } else if (Array.isArray(strategicOptionsData)) {
        optionsArray = strategicOptionsData;
      } else {
        // Handle object or other types
        optionsArray = [strategicOptionsData.toString()];
      }
      
      // Clean up and validate options
      optionsArray = optionsArray
        .map(option => {
          if (typeof option === 'string') {
            return option.trim();
          }
          return option.toString().trim();
        })
        .filter(option => option.length > 0 && option !== 'null' && option !== 'undefined');
        
    } catch (error) {
      console.error('Error parsing strategic options:', error);
      console.log('Strategic options data:', strategicOptionsData);
      
      // Fallback to treating as single string
      if (strategicOptionsData && strategicOptionsData.toString().trim()) {
        optionsArray = [strategicOptionsData.toString().trim()];
      }
    }
    
    return optionsArray;
  };

  // Extract the final paragraph from Act 3 to use as the decision question -push
  const getDecisionQuestion = () => {
    if (!acts[2] || !acts[2].content) return "What would you do?";
    
    const act3Content = acts[2].content;
    
    // If it's HTML content, extract the last paragraph
    if (act3Content.includes('<p>')) {
      const paragraphs = act3Content.match(/<p[^>]*>(.*?)<\/p>/gi);
      if (paragraphs && paragraphs.length > 0) {
        const lastParagraph = paragraphs[paragraphs.length - 1];
        // Strip HTML tags and return clean text
        return lastParagraph.replace(/<[^>]*>/g, '').trim();
      }
    }
    
    // If it's plain text, get the last paragraph
    const paragraphs = act3Content.split('\n').filter(p => p.trim().length > 0);
    if (paragraphs.length > 0) {
      return paragraphs[paragraphs.length - 1].trim();
    }
    
    return "What would you do?";
  };

  if (loading) {
    return (
      <div className="case-study-loading">
        <div className="loading-spinner"></div>
        <p>Loading case study...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="case-study-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={handleBack} className="btn btn-primary">
          Back to Cases
        </button>
      </div>
    );
  }

  if (!caseStudy) {
    return (
      <div className="case-study-error">
        <h2>Case Study Not Found</h2>
        <p>The requested case study could not be found.</p>
        <button onClick={handleBack} className="btn btn-primary">
          Back to Cases
        </button>
      </div>
    );
  }

  // Keep fixed 4 slots so Act I–IV section indices stay aligned.
  // Filtering empty acts collapsed the array (e.g. only Act IV survived →
  // it appeared under Act I, and Act II/III/IV showed "not available").
  const acts = [
    {
      number: 1,
      title: caseStudy.acts?.act_1?.title || 'Act I',
      content: caseStudy.acts?.act_1?.content
    },
    {
      number: 2,
      title: caseStudy.acts?.act_2?.title || 'Act II',
      content: caseStudy.acts?.act_2?.content
    },
    {
      number: 3,
      title: caseStudy.acts?.act_3?.title || 'Act III',
      content: caseStudy.acts?.act_3?.content
    },
    {
      number: 4,
      title: caseStudy.acts?.act_4?.title || 'Act IV',
      content: caseStudy.acts?.act_4?.content
    }
  ].map((act) => {
    const text = act.content && String(act.content).replace(/<[^>]*>/g, '').trim();
    return text ? act : { ...act, content: null, title: act.title };
  });
  const actHasContent = (act) =>
    !!(act?.content && String(act.content).replace(/<[^>]*>/g, '').trim());

  // FIXED: Use the new parsing function for strategic options
  const strategicOptions = parseStrategicOptions(caseStudy.strategic_options);

  const renderOptionGate = () => (
    <section className="decision-section-animated natively-scrollable">
      <div className="decision-wrapper">
        <div className="section-eyebrow">{railSections[currentSection]?.label}</div>
        <div className="decision-header-block">
          <h2 className="decision-main-title">Select an option to continue</h2>
          <p className="decision-main-subtitle">
            Choose one of the options on the Decision page before continuing.
          </p>
        </div>
        <button
          type="button"
          className="decision-submit-btn option-gate-back-btn"
          onClick={() => setCurrentSection(decisionSection)}
        >
          Go to Decision
        </button>
      </div>
    </section>
  );

  const renderSection = () => {
    if (currentSection === 0) {
      // Apple TV Style Hero section
      return (
        <section className="apple-hero-section">
          <div className="apple-hero-image-container">
            <img 
              src={caseStudy.images?.large || 
                   caseStudy.images?.medium || 
                   caseStudy.images?.small ||
                   'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&h=1080&fit=crop&auto=format&q=80'} 
              alt={caseStudy.title}
              className="apple-hero-image"
            />
            <div className="apple-hero-overlay">
              <div className="apple-hero-content">
                <div className="apple-title-section">
                  <h1 className="apple-hero-title">{caseStudy.title}</h1>
                  <div className="apple-meta">
                    <span className="apple-genre">{caseStudy.subject || 'Business Case Study'}</span>
                    <span className="apple-duration">15 min</span>
                  </div>
                  {/* Render description as HTML if it contains HTML tags */}
                  {caseStudy.description && caseStudy.description.includes('<') ? 
                    renderHTMLContent(caseStudy.description) :
                    <p className="apple-description">{caseStudy.description}</p>
                  }
                </div>
                <div className="apple-actions">
                  <button className="apple-read-case-btn" onClick={handleReadCase}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M4 4h9a3 3 0 0 1 3 3v13a2 2 0 0 0-2-2H4V4z" stroke="currentColor" strokeWidth="2"/>
                      <path d="M20 4h-3a3 3 0 0 0-3 3v13a2 2 0 0 1 2-2h4V4z" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    Read Case
                  </button>
                  <button
                    className="apple-watch-btn"
                    onClick={handleWatchFilm}
                    style={{ opacity: 0.6, cursor: 'pointer' }}
                    title="Film coming soon"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    Watch Film
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      );
    } else if (currentSection === 1 || currentSection === 2 || currentSection === 3) {
      const actIndex = currentSection - 1;
      const act = acts[actIndex];
      // Strip the cliffhanger <ol> options from Act III's narrative — the
      // Decision section shows strategic_options separately, so showing the
      // raw list here too was redundant. No-op for Acts I/II, which have no
      // <ol> in their content.
      const displayContent = act?.content
        ? act.content
            .replace(/^#{1,6}\s*Act\s*[IVX0-9]+\s*$/gim, '')
            .replace(/^#{1,6}\s*Act\s*[IVX0-9]+\s*:\s*.*$/gim, '')
            .replace(/<ol[^>]*>[\s\S]*?<\/ol>/gi, '')
        : '';

      const cleanedContent = displayContent;

      return (
        <section className="acts-horizontal-section natively-scrollable" data-tour={`case-act-${actIndex}`}>
          <div className="acts-container">
            <div className="section-eyebrow">{railSections[currentSection]?.label}</div>
            {actHasContent(act) ? (
              <div className="act-content-horizontal">
                <div className="act-text-animated">
                  <h3 className="act-title-horizontal">{stripSectionLabelPrefix(act.title)}</h3>
                  <div className="act-text-content natively-scrollable">
                    {cleanedContent.includes('<')
                      ? renderHTMLContent(cleanedContent)
                      : <p>{cleanedContent}</p>
                    }
                  </div>
                </div>
              </div>
            ) : (
              <div className="exhibits-slide-empty">
                <p>This case material is not available.</p>
              </div>
            )}
          </div>
        </section>
      );
    } else if (currentSection >= exhibitStartSection && currentSection < decisionSection) {
    // Individual Exhibit section
    const exhibitIndex = currentSection - exhibitStartSection;
    const exhibit = figures[exhibitIndex];

    return (
      <section className="exhibits-slide-section natively-scrollable" data-tour="case-exhibit-section">
        <div className="exhibits-slide-container natively-scrollable">
          <div className="section-eyebrow">{railSections[currentSection]?.label}</div>
          {exhibit ? (
            <div className="exhibit-single-page">
              <div className="exhibits-slide-header">

                <h2 className="exhibits-slide-title">
                  {stripSectionLabelPrefix(stripHTMLTags(exhibit.title))}
                </h2>

                <div className="section-divider" />

                {exhibit.description && (
                  <p className="exhibits-slide-subtitle">
                    {stripHTMLTags(exhibit.description)}
                  </p>
                )}
              </div>

              {figureHasDisplayableImage(exhibit) && (
                <div className="exhibit-slide-image">
                  <img
                    src={String(exhibit.image_url).trim()}
                    alt={stripHTMLTags(exhibit.title)}
                  />
                </div>
              )}

              {figureHasDisplayableHtml(exhibit) && (
                <div className="act-text-content">
                  {renderHTMLContent(
                    getExhibitHtmlForDisplay(exhibit, exhibitIndex, caseStudy?.subject)
                  )}
                </div>
              )}

              {exhibit.notes && (
                <div className="exhibit-notes">
                  <h4>Notes:</h4>
                  {exhibit.notes.includes('<')
                    ? renderHTMLContent(exhibit.notes)
                    : <p>{exhibit.notes}</p>
                  }
                </div>
              )}

              {exhibit.source && (
                <div className="exhibit-source">
                  <h4>Source:</h4>
                  {exhibit.source.includes('<')
                    ? renderHTMLContent(exhibit.source)
                    : <p>{exhibit.source}</p>
                  }
                </div>
              )}

            </div>
          ) : (
            <div className="exhibits-slide-empty">
              <p>This exhibit is not available.</p>
            </div>
          )}
        </div>
      </section>
    );
    } else if (currentSection === decisionSection) {
      // Decision section with unified side-by-side layout
      const decisionPrompt = getDecisionQuestion();

      return (
        <section className="decision-section-animated natively-scrollable" data-tour="case-decision">
          {showFeedback ? (
            <div className="feedback-section natively-scrollable">
              <div className="feedback-header">
                <div className="section-eyebrow section-eyebrow--centered">{railSections[currentSection]?.label}</div>
                <h3>Your Analysis</h3>
                {strategicOptions.length > 0 && selectedOption !== null && (
                  <div className="selected-option-display">
                    <span className="option-label">You selected:</span>
                    <span className="option-text">{strategicOptions[selectedOption]}</span>
                  </div>
                )}
                <div className="reasoning-display">
                  <span className="reasoning-label">Your reasoning:</span>
                  <p className="reasoning-text">{decision}</p>
                </div>
              </div>
              
              <div className="feedback-content">
                <h4>Feedback</h4>
                {feedbackLoading ? (
                  <div className="feedback-loading">
                    <div className="loading-spinner"></div>
                    <p>{feedbackLoadingMessage}</p>
                  </div>
                ) : feedbackStructured ? (
                  <div className="feedback-text">
                    {renderStructuredFeedback(feedbackStructured)}
                  </div>
                ) : (
                  <div className="feedback-text">
                    {renderCaseStudyFeedback(feedback)}
                  </div>
                )}
              </div>

              <div className="feedback-actions" data-tour="case-tutorial">
                {quizCounts.total > 0 ? (
                  <button
                    id="case-continue-quizzes-btn"
                    onClick={handleStartCaseQuizzes}
                    className="decision-submit-btn"
                  >
                    Continue to Reasoning &amp; Knowledge Tests
                  </button>
                ) : null}
                <button 
                  id="case-continue-tutorial-btn"
                  onClick={() => handleStartCaseInterview('tutorial')} 
                  className="decision-submit-btn"
                >
                  Continue to Tutorial
                </button>
                <button 
                  id="case-continue-harvard-btn"
                  onClick={() => handleStartCaseInterview('harvard')} 
                  className="decision-submit-btn"
                >
                  Harvard Case Method
                </button>
                <button 
                  id="case-try-again-btn"
                  onClick={handleNewAttempt} 
                  className="try-again-btn"
                >
                  Try Another Analysis
                </button>
              </div>
            </div>
          ) : (
            <div className="decision-wrapper">
              <div className="section-eyebrow">{railSections[currentSection]?.label}</div>
              <div className="decision-header-block">
                <h2 className="decision-main-title">{decisionPrompt || 'Decision'}</h2>
                <div className="section-divider" />
                {strategicOptions.length > 0 && (
                  <div className="decision-column-label case-assessment-kicker case-assessment-kicker--bold">Select Your Preferred Ruling / Action</div>
                )}
              </div>

              <div className={`decision-container ${strategicOptions.length > 0 ? 'has-split-layout' : 'single-column'}`}>
                {/* Left Column: Strategic Options */}
                {strategicOptions.length > 0 && (
                  <div className="strategic-options-animated">
                    <div className="options-list natively-scrollable">
                      {strategicOptions.map((option, index) => {
                        // Remove any numbering already present in the source text
                        
                        const cleanedOption = String(option)
                          .replace(/^\s*(?:\*\*)?\d+\.(?:\*\*)?\s*/, '')
                          .trim();

                        const match = cleanedOption.match(/^(.+?)([.:!?])(\s+)([\s\S]*)$/);

                        return (
                          <button
                            key={index}
                            ref={(el) => { optionCardRefs.current[index] = el; }}
                            type="button"
                            className={`strategic-option-card ${
                              selectedOption === index ? 'selected' : ''
                            }`}
                            onClick={() => setSelectedOption((prev) => (prev === index ? null : index))}
                          >
                            <div className="option-number">
                              {index + 1}
                            </div>

                            <div className="option-text">
                              {match ? (
                                formatInlineBold(
                                  `**${match[1]}${match[2]}**\n\n${match[4].replace(
                                    /([.:!?]+)(\s+)(Achieves|Trades off)/g,
                                    '$1\n\n$3'
                                  )}`
                                )
                              ) : (
                                <strong>{cleanedOption}</strong>
                              )}
                            </div>

                            <div className="option-indicator" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

              </div>
            </div>
          )}
          </section>
        );
    } else if (currentSection === quizSection && strategicOptions.length > 0 && selectedOption === null) {
      return renderOptionGate();
    } else if (currentSection === quizSection) {
      return (
        <CaseQuizPanel
          caseStudyId={id}
          onBackToDecision={() => setCurrentSection(actIVSection)}
          onContinueToWriting={() => setCurrentSection(oxfordTutorialSection)}
        />
      );
    } else if (currentSection === actIVSection && strategicOptions.length > 0 && selectedOption === null) {
      return renderOptionGate();
    } else if (currentSection === actIVSection) {
      const act = acts[3];

      console.log('ACT III RAW CONTENT:', act?.content);

      return (
        <section className="acts-horizontal-section natively-scrollable" data-tour="case-act-3">
          <div className="acts-container">
            <div className="section-eyebrow">{railSections[currentSection]?.label}</div>
            {actHasContent(act) ? (
              <div className="act-content-horizontal">
                <div className="act-text-animated">

                  <h3 className="act-title-horizontal">
                    {stripSectionLabelPrefix(act.title)}
                  </h3>

                  <div className="act-text-content natively-scrollable">
                    {act.content && act.content.includes('<')
                      ? renderHTMLContent(act.content)
                      : <p>{act.content}</p>
                    }
                  </div>
                </div>

              </div>
            ) : (
              <div className="exhibits-slide-empty">
                <p>This case material is not available.</p>
              </div>
            )}
          </div>
        </section>
      ); 
    }else if (currentSection === writingTaskSection && strategicOptions.length > 0 && selectedOption === null) {
      return renderOptionGate();
    }else if (currentSection === writingTaskSection) {
      return (
        <section className="decision-section-animated natively-scrollable">
          <div className="decision-wrapper">
            <div className="section-eyebrow">{railSections[currentSection]?.label}</div>
            <div className="decision-header-block">
              <h2 className="decision-main-title">Explain Your Decision</h2>
              <div className="section-divider" />
              <p className="decision-main-subtitle writing-task-subtitle">
                Explain the reasoning behind your decision. Aim for about 100-200 words.
              </p>
            </div>

            <div className="decision-input-animated">
              <div className="decision-input-top">

                {strategicOptions.length > 0 && selectedOption !== null ? (
                  <span className="decision-selected-label">
                    Option {selectedOption + 1} Selected
                  </span>
                ) : null}
              </div>

              <form className="decision-form" onSubmit={handleDecisionSubmit}>
                <div className="rich-text-editor">
                  <div className="rich-text-toolbar" role="toolbar" aria-label="Text formatting">
                    <button
                      type="button"
                      className="rich-text-toolbar-btn"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        document.execCommand('bold', false);
                      }}
                      disabled={submittingDecision}
                      aria-label="Bold"
                      title="Bold"
                    >
                      <strong>B</strong>
                    </button>

                    <button
                      type="button"
                      className="rich-text-toolbar-btn"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        document.execCommand('italic', false);
                      }}
                      disabled={submittingDecision}
                      aria-label="Italic"
                      title="Italic"
                    >
                      <em>I</em>
                    </button>

                    <button
                      type="button"
                      className="rich-text-toolbar-btn"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        document.execCommand('underline', false);
                      }}
                      disabled={submittingDecision}
                      aria-label="Underline"
                      title="Underline"
                    >
                      <u>U</u>
                    </button>
                  </div>

                  <div
                    ref={decisionEditorRef}
                    id="case-reasoning-editor"
                    className="rich-text-editor-content"
                    contentEditable={!submittingDecision}
                    suppressContentEditableWarning
                    data-placeholder={
                      strategicOptions.length > 0
                        ? "Explain the logic, legal/business principles, tradeoffs, and evidence behind your choice..."
                        : "What would you have done in this situation? Explain your reasoning and decision-making process..."
                    }
                    onInput={(e) => {
                      setDecision(e.currentTarget.innerHTML);
                    }}
                    onBlur={(e) => {
                      setDecision(e.currentTarget.innerHTML);
                    }}
                  />
                </div>

                <button
                  id="case-decision-submit-btn"
                  type="submit"
                  className="decision-submit-btn"
                  disabled={
                    submittingDecision ||
                    !decision.trim() ||
                    (strategicOptions.length > 0 && selectedOption === null)
                  }
                >
                  {submittingDecision
                    ? 'Getting feedback...'
                    : 'Submit Decision'}
                </button>
              </form>
            </div>
          </div>
        </section>
      );
    } else if (currentSection === oxfordTutorialSection && strategicOptions.length > 0 && selectedOption === null) {
      return renderOptionGate();
    } else if (currentSection === oxfordTutorialSection) {
      return (
        <CaseTutorialPanel
          caseStudyId={id}
          tutorialPreload={tutorialPreloadRef.current}
          onContinueToDiscussion={() => setCurrentSection(harvardDiscussionSection)}
          onBackToCase={() => setCurrentSection(quizSection)}
        />
      );
    } else if (currentSection === harvardDiscussionSection && strategicOptions.length > 0 && selectedOption === null) {
      return renderOptionGate();
    } else if (currentSection === harvardDiscussionSection) {
      return (
        <CaseDiscussionPanel
          caseStudyId={id}
          discussionPreload={discussionPreloadRef.current}
          userName={userName}
          onBackToTutorial={() => setCurrentSection(oxfordTutorialSection)}
          onBackToCase={() => setCurrentSection(0)}
        />
      );
    }
  };

  return (
    <div className="case-study-page">
      {/* Main Content */}
      <main className={`case-study-main natively-scrollable ${currentSection === 0 ? 'case-study-main--no-rail' : ''} ${isMainTransitioning ? 'case-study-main--transitioning' : ''}`} ref={mainContentRef} tabIndex={-1}>
        <div className={`case-study-section-transition case-study-section-transition--${sectionTransitionPhase}`}>
          {renderSection()}
        </div>
      </main>

      {/* Section Navigation — hidden on Film, which has no working rail-based
          navigation yet (its only control, "Watch Film", is disabled). */}
      {currentSection !== 0 && (
        <nav className="section-navigation" aria-label="Case study sections">
          <div className="rail-track" aria-hidden="true"></div>
          <div className="rail-fill" aria-hidden="true" ref={railFillRef}></div>
          <div className={`section-indicators ${railScrollable ? 'section-indicators--scrollable natively-scrollable' : ''}`} ref={railListRef}>
            {railSections.map(({ label, icon, index, locked }) => (
              <button
                key={index}
                ref={(el) => { railItemRefs.current[index] = el; }}
                type="button"
                className={`section-indicator ${currentSection === index ? 'active' : ''} ${locked ? 'locked' : ''}`}
                onClick={() => {
                  if (!locked) {
                    setCurrentSection(index);
                  }
                }}
                aria-label={`Go to ${label}`}
                aria-current={currentSection === index ? 'true' : undefined}
                disabled={locked}
              >
                <span className="section-indicator-icon">{SECTION_ICONS[icon]}</span>
                <span className="section-label">{label}</span>
              </button>
            ))}
          </div>
        </nav>
      )}
      {/* Exhibit Modal */}
      {exhibitModalOpen && selectedExhibit && (
        <div className="exhibit-modal-backdrop" onClick={closeExhibitModal}>
          <div className="exhibit-modal" data-tour="case-exhibit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="exhibit-modal-header">
              <h3>{stripHTMLTags(selectedExhibit.title)}</h3>
              <button className="exhibit-modal-close" onClick={closeExhibitModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </button>
            </div>
            <div className="exhibit-modal-content natively-scrollable">
              {stripHtmlToText(selectedExhibit.description || '').length > 0 && (
                <div className="exhibit-text-content">
                  <h4>Description:</h4>
                  {selectedExhibit.description.includes('<') ?
                    renderHTMLContent(selectedExhibit.description) :
                    <p>{selectedExhibit.description}</p>
                  }
                </div>
              )}
              {figureHasDisplayableHtml(selectedExhibit) &&
                renderHTMLContent(
                  getExhibitHtmlForDisplay(
                    selectedExhibit,
                    figures.findIndex((f) => f.id === selectedExhibit.id),
                    caseStudy?.subject
                  )
                )}

              {/* Additional exhibit information */}
              {selectedExhibit.notes && (
                <div className="exhibit-notes">
                  <h4>Notes:</h4>
                  {selectedExhibit.notes.includes('<') ? 
                    renderHTMLContent(selectedExhibit.notes) :
                    <p>{selectedExhibit.notes}</p>
                  }
                </div>
              )}
              
              {selectedExhibit.source && (
                <div className="exhibit-source">
                  <h4>Source:</h4>
                  {selectedExhibit.source.includes('<') ? 
                    renderHTMLContent(selectedExhibit.source) :
                    <p>{selectedExhibit.source}</p>
                  }
                </div>
              )}

              {figureHasDisplayableImage(selectedExhibit) && (
                <div className="exhibit-diagrams">
                  <h4>Diagrams</h4>
                  <div className="exhibit-diagrams-grid">
                    <button
                      type="button"
                      className="exhibit-diagram-thumb"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDiagramFromExhibit(
                          selectedExhibit.image_url,
                          stripHTMLTags(selectedExhibit.title)
                        );
                      }}
                      aria-label={`Open diagram: ${stripHTMLTags(selectedExhibit.title)}`}
                    >
                      <img
                        src={String(selectedExhibit.image_url).trim()}
                        alt=""
                      />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={`watch-film-toast${watchFilmToastVisible ? ' show' : ''}`} role="status" aria-live="polite">
        <span>Film coming soon — check back later.</span>
      </div>

      {diagramViewer && (
        <div
          className="diagram-viewer-backdrop"
          role="presentation"
          onClick={closeDiagramViewer}
        >
          <div
            className="diagram-viewer"
            role="dialog"
            aria-modal="true"
            aria-label={diagramViewer.title}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="diagram-viewer-header">
              <span className="diagram-viewer-title">{diagramViewer.title}</span>
              <button
                type="button"
                className="diagram-viewer-close"
                onClick={closeDiagramViewer}
                aria-label="Close diagram"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </button>
            </div>
            <div className="diagram-viewer-body natively-scrollable">
              <img src={diagramViewer.url} alt={diagramViewer.title} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseStudy;